import { provideDiTokenValue } from '@bcf-vanilla-ts-v1-shared/di/di-token';
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import { LOCATION } from '@bcflit-v1-configurators/tokens/app/token';
import { isServer } from 'lit';

type LocationKind = {
  origin: string;
  pathname: string;
  search: string;
  hash: string;
};

type RouterGoingBackEvent = CustomEvent<{ count: number }>;

function mapToFullPath(location: LocationKind): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

export class HistoryUrl {
  private _history: string[] = [];
  private _skipNextPopState: boolean = false;

  constructor(private _location: Location) {
    if (!isServer) {
      window.addEventListener('popstate', () => this._popState());
      window.addEventListener('hashchange', () => this._checkAtNextTick());
      window.addEventListener('routerUpdated', () => this._checkUrl());
      window.addEventListener('routerReplaced', () => this._replaceAtNextTick());
      window.addEventListener('routerGoingBack', (e: Event) => this._popState(e as RouterGoingBackEvent));
    } else {
      this._checkUrl();
    }

    this._history = this._getFromMemory();
  }

  private _checkAtNextTick(): void {
    setTimeout(() => {
      this._checkUrl();
    }, 10);
  }

  private _replaceAtNextTick(): void {
    setTimeout(() => {
      this._replaceUrl();
    }, 10);
  }

  private _replaceUrl(): void {
    const newPath: string = mapToFullPath(this._location);
    if (this._history.length === 0) {
      this._history.push(newPath);
    } else {
      this._history[this._history.length - 1] = newPath;
    }
    this._updateMemory();
  }

  private _checkUrl(): void {
    const newPath: string = mapToFullPath(this._location);
    const lastHistoryUrl: string | undefined = this._history[this._history.length - 1];
    if (lastHistoryUrl === newPath) {
      return;
    }
    this._history.push(newPath);
    this._updateMemory();
  }

  private _popState(event?: RouterGoingBackEvent): void {
    if (this._skipNextPopState) {
      this._skipNextPopState = false;
      return;
    }
    if (event) {
      this._skipNextPopState = true;
      const count: number = event.detail.count ?? 1;
      this._history = this._history.slice(0, -count);
      this._updateMemory();
      return;
    }
    const current: string = mapToFullPath(this._location);
    const idx: number = this._history.lastIndexOf(current);
    if (idx >= 0) {
      this._history = this._history.slice(0, idx + 1);
    } else {
      this._history.push(current);
    }
    this._updateMemory();
  }

  public get history(): string[] {
    return this._history.slice();
  }

  private _updateMemory(): void {
    if (!isServer) {
      try {
        window.sessionStorage.setItem('historyUrls', JSON.stringify(this.history));
      } catch {}
    }
  }

  private _getFromMemory(): string[] {
    if (isServer) {
      return [];
    }
    try {
      const fromMemory: string | undefined = window.sessionStorage.getItem('historyUrls') ?? undefined;
      if (fromMemory) {
        return JSON.parse(fromMemory);
      }
      return [];
    } catch {
      return [];
    }
  }
}

export function provideHistoryUrl(): HistoryUrl {
  return provideSingleton(HistoryUrl, () => new HistoryUrl(provideDiTokenValue(LOCATION)));
}
